import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import DesignEditorLoadingIndicator from '../../../../components/global/DesignEditor/components/DesignEditorLoadingIndicator';
import DesignEditor from '../../../../components/global/DesignEditor';
import { ORIENTATION } from '../../../cards/constants';
import PrintProductGroupConfigurationItem from './components/PrintProductGroupConfigurationItem';
import PrintProductsGroupConfiguratorHeaderPane from './components/PrintProductsGroupConfiguratorHeaderPane';
import DesignEditorPreviewPane from '../../../../components/global/DesignEditor/components/DesignEditorPreviewPane';
import Button from '../../../../components/global/Button';
import { getPhotoCardElements } from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/AccountCardAlbumCustomPrintOption/AccountCardAlbumCustomPrintConfigurator/components/AccountCardEmptyTextAlertModal/helpers';
import { getBackgroundAccountCardElement } from '../../../cards/views/AccountCardConfigurator/helpers';

const propTypes = {
  className: PropTypes.string,
  printProducts: PropTypes.arrayOf(
    PropTypes.shape({
      printProductId: PropTypes.string,
      printProductSizeId: PropTypes.string,
      orientation: PropTypes.oneOf(Object.values(ORIENTATION)),
      defaultAccountCard: PropTypes.shape({
        id: PropTypes.string,
      }),
    }),
  ),
  photo: PropTypes.shape({
    id: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  buttonText: PropTypes.node,
  albumMembershipGroup: PropTypes.shape({
    id: PropTypes.string.isRequired,
    is_album: PropTypes.bool,
  }),
  identity: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  printBackgrounds: PropTypes.arrayOf({
    id: PropTypes.string,
    asset_image: PropTypes.shape({
      url: PropTypes.string,
    }),
  }),
};

const PrintProductGroupConfigurator = ({
  className,
  photo,
  onClose,
  onComplete,
  albumMembershipGroup,
  identity,
  printProducts,
  printBackgrounds,
}) => {
  const [accountCardConfigurations, setAccountCardConfigurations] = useState(
    [],
  );
  const [activeItem, setActiveItem] = useState(0);
  const [printBackgroundModalOpen, setPrintBackgroundModalOpen] = useState(
    false,
  );
  const printProductItem = printProducts[activeItem];
  const { printProductSizeId } = printProductItem ?? {};
  const isLastPrintProductItem = activeItem + 1 === printProducts.length;

  const classes = classNames({
    [className]: !!className,
    'print-product-configurator': true,
    'print-product-group-configurator': true,
  });

  if (!printProducts) {
    return <DesignEditorLoadingIndicator />;
  }

  const handleNextItem = () => {
    if (!isLastPrintProductItem) {
      setActiveItem(activeItem + 1);
    } else {
      onComplete(accountCardConfigurations);
    }
  };

  const isMissingPhotos = accountCardConfigurations.find(
    config =>
      getPhotoCardElements({
        accountCard: config?.accountCard,
        emptyOnly: true,
      }).length !== 0,
  );

  const activeAccountCard = accountCardConfigurations.find(
    ({ printProductSize }) => printProductSize.id === printProductSizeId,
  )?.accountCard;

  const backgroundElement = getBackgroundAccountCardElement({
    accountCard: activeAccountCard,
  });

  const canCompleteSetup = !(
    (accountCardConfigurations?.length ?? 0) === 0 ||
    accountCardConfigurations.find(config => !config.accountCard) ||
    isMissingPhotos
  );
  const hasPrintBackgrounds =
    !!backgroundElement && printBackgrounds?.length > 0;

  return (
    <DesignEditor className={classes}>
      <PrintProductsGroupConfiguratorHeaderPane
        onClose={onClose}
        activeItem={activeItem}
        printProductItem={printProductItem}
        totalAmount={printProducts.length}
      />

      <DesignEditorPreviewPane className="print-product-group-configurator-preview-pane">
        <h3 className="my-1 text-center">
          Please tap on your photo below to adjust &amp; crop.
        </h3>
        {hasPrintBackgrounds && (
          <div className="print-product-group-configurator-backdrop-action-container">
            <Button
              btnStyle="link"
              btnSize="sm"
              className="text-extra-bold"
              onClick={() => setPrintBackgroundModalOpen(true)}
            >
              {backgroundElement?.cardElementConfiguration?.asset_image?.id
                ? 'Change Backdrop'
                : 'Add Backdrop'}
            </Button>
          </div>
        )}
        <PrintProductGroupConfigurationItem
          key={printProductItem.printProductSizeId}
          photo={photo}
          albumMembershipGroup={albumMembershipGroup}
          identity={identity}
          printBackgrounds={printBackgrounds}
          printBackgroundModalOpen={printBackgroundModalOpen}
          setPrintBackgroundModalOpen={setPrintBackgroundModalOpen}
          onAccountCardUpdated={({
            printProduct,
            printProductSize,
            accountCard,
          }) => {
            if (!printProductSize) {
              return;
            }

            const updatedConfig = {
              printProduct,
              printProductSize,
              accountCard,
            };

            const activeConfiguration = accountCardConfigurations.find(
              config => config.printProductSize?.id === printProductSize?.id,
            );

            if (activeConfiguration) {
              setAccountCardConfigurations(current => [
                ...current.filter(
                  config =>
                    config.printProductSize?.id !== printProductSize?.id,
                ),
                updatedConfig,
              ]);
            } else {
              setAccountCardConfigurations(current => [
                ...current,
                updatedConfig,
              ]);
            }
          }}
          {...printProductItem}
        />
        {canCompleteSetup && (
          <div className="print-product-group-configurator-continue-wrap">
            <p className="text-center mb-3 text-dark">
              <span className="text-danger text-uppercase text-extra-bold">
                Important:
              </span>
              <br />
              This is how the final product will look.
            </p>
            {canCompleteSetup && (
              <Button
                btnBlock
                btnSize="lg"
                onClick={handleNextItem}
                btnStyle="primary"
                data-testid="print-product-group-configurator-continue"
                className="print-product-group-configurator-continue"
              >
                I&apos;m good with how this looks!
                <div className="print-product-group-configurator-continue-sub-text">
                  Continue ({activeItem + 1}/{printProducts.length})
                </div>
              </Button>
            )}
          </div>
        )}
      </DesignEditorPreviewPane>
    </DesignEditor>
  );
};

PrintProductGroupConfigurator.propTypes = propTypes;

export default PrintProductGroupConfigurator;
